import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useColors } from "../constants/Colors";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import AuthModal from "../components/AuthModal";
import TabSwitcher from "../components/TabSwitcher";
import PageLayout from "../components/PageLayout";
import EmptyState from "../components/EmptyState";
import { trpcReact } from "../trpc_client";
import FullscreenVideoModal from "../components/FullscreenVideoModal";
import FilterTags from "../components/FilterTags";
import ApplicationGridItem, {
  Application,
  ApplicationLike,
} from "../components/ApplicationGridItem";
import { Maximize2 } from "lucide-react";

// Application filter type
interface ApplicationFilters {
  video: boolean;
  text: boolean;
  accepted: boolean;
  inactive: boolean;
}

const defaultApplicationFilters: ApplicationFilters = {
  video: false,
  text: false,
  accepted: false,
  inactive: false,
};

const ApplicationsPlaceholder: React.FC = () => {
  const { t } = useTranslation();
  const colors = useColors();
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const { userExistsInDb } = useProfile();
  const [showAuth, setShowAuth] = useState(false);
  const [activeTab, setActiveTab] = useState<"sent" | "received">(() => {
    const saved = sessionStorage.getItem("applications_active_tab");
    return saved === "received" ? "received" : "sent";
  });
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | undefined>(
    undefined,
  );
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [filters, setFilters] = useState<ApplicationFilters>(
    defaultApplicationFilters,
  );
  const [localLikes, setLocalLikes] = useState<
    Record<number, ApplicationLike[]>
  >({});

  // Persist active tab to sessionStorage
  useEffect(() => {
    sessionStorage.setItem("applications_active_tab", activeTab);
  }, [activeTab]);

  // Toggle a specific filter (only one can be active at a time)
  const toggleFilter = (key: keyof ApplicationFilters) => {
    setFilters((prev) => {
      // If clicking the same filter that's already active, deselect it
      if (prev[key]) {
        return defaultApplicationFilters;
      }
      // Otherwise, activate only this filter (deselect all others)
      return {
        ...defaultApplicationFilters,
        [key]: true,
      };
    });
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters(defaultApplicationFilters);
  };

  // Fetch auth token for video playback
  useEffect(() => {
    const fetchToken = async () => {
      if (user) {
        const token = await getIdToken();
        setAuthToken(token);
      }
    };
    fetchToken();
  }, [user, getIdToken]);

  const { data: currentUser } = trpcReact.getCurrentUserProfile.useQuery(
    undefined,
    { enabled: userExistsInDb },
  );

  // Fetch sent applications (applications by the current user)
  const {
    data: sentApplications,
    isLoading: sentLoading,
    error: sentError,
    refetch: refetchSentApplications,
  } = trpcReact.getApplicationsByUser.useQuery(undefined, {
    enabled: userExistsInDb,
  });

  // Fetch flat profiles for sent applications (like the native app)
  const { data: flatProfilesData, isLoading: flatProfilesLoading } =
    trpcReact.getFlatProfilesOfAppliedFlats.useQuery(undefined, {
      enabled: userExistsInDb,
    });

  // Create a map of application_id -> flat profile for quick lookup
  const flatProfilesMap = useMemo(() => {
    if (!flatProfilesData?.flatProfiles) return {};
    const map: Record<
      number,
      { flat_id: number; name: string; image: string | null }
    > = {};
    for (const profile of flatProfilesData.flatProfiles) {
      if (profile.application_id && profile.flat) {
        map[profile.application_id] = {
          flat_id: profile.flat.flat_id,
          name: profile.flat.name || "Unknown Flat",
          image: profile.urls?.[0] || null,
        };
      }
    }
    return map;
  }, [flatProfilesData]);

  // Fetch received applications (applications to user's flats)
  const {
    data: receivedApplications,
    isLoading: receivedLoading,
    error: receivedError,
    refetch: refetchReceivedApplications,
  } = trpcReact.getRoomApplications.useQuery(undefined, {
    enabled: userExistsInDb,
  });

  const likeApplicationMutation = trpcReact.likeApplication.useMutation({
    onMutate: ({ application_id }) => {
      const currentUserId = currentUser?.user_id;
      if (!currentUserId) return;
      setLocalLikes((prev) => {
        const existing =
          prev[application_id] ||
          ([
            ...((sentApplications as Application[] | undefined)?.filter(
              (app) => app.application_id === application_id,
            ) ?? []),
            ...(receivedApplications
              ?.flatMap((room) => room.applications)
              .filter((app) => app.application_id === application_id) || []),
          ][0]?.application_likes as ApplicationLike[] | undefined) ||
          [];
        const isLiked = existing.some((like) => like.user_id === currentUserId);
        return {
          ...prev,
          [application_id]: isLiked
            ? existing.filter((like) => like.user_id !== currentUserId)
            : [...existing, { application_id, user_id: currentUserId }],
        };
      });
    },
    onError: (_error, { application_id }) => {
      setLocalLikes((prev) => {
        const next = { ...prev };
        delete next[application_id];
        return next;
      });
    },
  });

  // Fetch applicant profiles for received applications (like the native app)
  const { data: applicantProfilesData, isLoading: applicantProfilesLoading } =
    trpcReact.getApplicantProfiles.useQuery(undefined, {
      enabled: userExistsInDb,
    });

  // Create a map of application_id -> applicant profile for quick lookup
  const applicantProfilesMap = useMemo(() => {
    if (!applicantProfilesData) return {};
    const map: Record<number, { name: string; image: string | null }> = {};
    for (const profile of applicantProfilesData) {
      if (profile.application_id) {
        map[profile.application_id] = {
          name: profile.username || "Unknown",
          image: profile.profile_images?.[0] || null,
        };
      }
    }
    return map;
  }, [applicantProfilesData]);

  const handleVideoClick = (url: string) => {
    setSelectedVideoUrl(url);
    setVideoModalOpen(true);
  };

  const handleCloseVideoModal = () => {
    setVideoModalOpen(false);
    setSelectedVideoUrl(undefined);
  };

  const getLikes = (application: Application): ApplicationLike[] =>
    localLikes[application.application_id] ??
    application.application_likes ??
    [];

  const handleLikeToggle = (applicationId: number) => {
    likeApplicationMutation.mutate({ application_id: applicationId });
  };

  // Open carousel view with all applications
  const openCarousel = (index: number = 0) => {
    const applications =
      activeTab === "sent"
        ? filteredSentApplications
        : filteredReceivedApplications;
    navigate("/applications/carousel", {
      state: {
        applications,
        initialIndex: index,
        type: activeTab,
      },
    });
  };

  // Handle click on application item - open in carousel
  const handleApplicationClick = (applicationId: number) => {
    const applications =
      activeTab === "sent"
        ? filteredSentApplications
        : filteredReceivedApplications;
    const index = applications.findIndex(
      (app) => app.application_id === applicationId,
    );
    if (index !== -1) {
      openCarousel(index);
    }
  };

  const isLoading =
    sentLoading ||
    receivedLoading ||
    flatProfilesLoading ||
    applicantProfilesLoading;

  // Flatten received applications for display
  const allReceivedApplications = useMemo(
    () => receivedApplications?.flatMap((room) => room.applications) || [],
    [receivedApplications],
  );

  // Filter applications based on active filters
  const filterApplications = (applications: Application[]) => {
    const anyFilterActive = Object.values(filters).some((v) => v);

    if (!anyFilterActive) {
      // No filters - hide inactive (declined, retracted) by default
      return applications.filter(
        (app) => app.state !== "declined" && app.state !== "retracted",
      );
    }

    return applications.filter((app) => {
      // Inactive filter (declined, retracted)
      if (filters.inactive) {
        if (app.state === "declined" || app.state === "retracted") return true;
      }

      // Skip inactive if that filter isn't set
      if (app.state === "declined" || app.state === "retracted") return false;

      // Video filter
      if (filters.video && app.type === "video") return true;

      // Text filter
      if (filters.text && app.type === "text") return true;

      // Accepted filter
      if (filters.accepted && app.state === "accepted") return true;

      return false;
    });
  };

  // Enrich sent applications with flat profile data
  const enrichedSentApplications = useMemo(() => {
    if (!sentApplications) return [];
    return (sentApplications as Application[]).map((app) => {
      const flatProfile = flatProfilesMap[app.application_id];
      return {
        ...app,
        flat_id: flatProfile?.flat_id,
        flat_name: flatProfile?.name,
        flat_image: flatProfile?.image,
      };
    });
  }, [sentApplications, flatProfilesMap]);

  const filteredSentApplications = useMemo(
    () => filterApplications(enrichedSentApplications),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enrichedSentApplications, filters],
  );

  // Enrich received applications with applicant profile data
  const enrichedReceivedApplications = useMemo(() => {
    return (allReceivedApplications as Application[]).map((app) => {
      const applicantProfile = applicantProfilesMap[app.application_id];
      return {
        ...app,
        applicant_name: applicantProfile?.name,
        applicant_image: applicantProfile?.image,
      };
    });
  }, [allReceivedApplications, applicantProfilesMap]);

  const filteredReceivedApplications = useMemo(
    () => filterApplications(enrichedReceivedApplications),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enrichedReceivedApplications, filters],
  );

  // Show sign-in prompt if not authenticated
  if (!user) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center"
        style={{ backgroundColor: colors.containerNeutralSolidSurface }}
      >
        <EmptyState
          type="no-sent-applications"
          title={t("applications_tab.tabbar_title") || "Applications"}
          description="Sign in to view and manage your applications."
          action={{
            label: t("sign_in") || "Sign in",
            onClick: () => setShowAuth(true),
          }}
        />
        <AuthModal
          isOpen={showAuth}
          onClose={() => setShowAuth(false)}
          onSuccess={() => setShowAuth(false)}
        />
      </div>
    );
  }

  return (
    <PageLayout
      title={t("applications_tab.tabbar_title") || "Applications"}
      maxWidth="max-w-7xl"
      headerContent={
        <TabSwitcher
          tabs={[
            {
              id: "sent",
              label: "Sent",
              count: filteredSentApplications.length,
            },
            {
              id: "received",
              label: "Received",
              count: filteredReceivedApplications.length,
            },
          ]}
          activeTab={activeTab}
          onTabChange={(tabId) => setActiveTab(tabId as "sent" | "received")}
        />
      }
      headerButton={
        ((activeTab === "sent" && filteredSentApplications.length > 0) ||
          (activeTab === "received" &&
            filteredReceivedApplications.length > 0)) ? (
          <button
            onClick={() => openCarousel(0)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-gnarly-semibold transition-all duration-150 hover:scale-[1.03] active:scale-[0.97] flex-shrink-0"
            style={{
              backgroundColor: colors.accentColor,
              color: colors.textNeutralOnPrimary,
            }}
            title={t("carousel_view") || "Carousel View"}
          >
            <Maximize2 size={15} />
            <span className="hidden sm:inline">
              {t("carousel_view") || "Carousel"}
            </span>
          </button>
        ) : undefined
      }
    >
      {/* Filter Tags */}
      <div className="mt-4 mb-4">
        <FilterTags
          filters={[
            {
              id: "video",
              label: t("filter_tags.video") || "Video",
              isSelected: filters.video,
              onPress: () => toggleFilter("video"),
            },
            {
              id: "text",
              label: t("filter_tags.text") || "Text",
              isSelected: filters.text,
              onPress: () => toggleFilter("text"),
            },
            {
              id: "accepted",
              label: t("filter_tags.accepted") || "Accepted",
              isSelected: filters.accepted,
              onPress: () => toggleFilter("accepted"),
            },
            {
              id: "inactive",
              label: t("filter_tags.inactive") || "Archived",
              isSelected: filters.inactive,
              onPress: () => toggleFilter("inactive"),
            },
          ]}
          onPressAll={resetFilters}
        />
      </div>

      {isLoading && (
        <div
          className="text-center py-12"
          style={{ color: colors.textNeutralMuted }}
        >
          Loading applications...
        </div>
      )}

      {/* Sent Applications Grid */}
      {!isLoading && activeTab === "sent" && (
        <>
          {filteredSentApplications.length === 0 ? (
            <EmptyState
              type={
                sentApplications?.length === 0
                  ? "no-sent-applications"
                  : "no-results"
              }
              title={
                sentApplications?.length === 0
                  ? undefined
                  : "No matching applications"
              }
              description={
                sentApplications?.length === 0
                  ? undefined
                  : "Try adjusting your filters to see more results."
              }
              action={
                sentApplications?.length === 0
                  ? {
                      label: "Browse flats",
                      onClick: () => navigate("/"),
                    }
                  : undefined
              }
            />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredSentApplications.map((application) => (
                <div key={application.application_id} className="space-y-2">
                  <ApplicationGridItem
                    application={application}
                    type="sent"
                    onVideoClick={handleVideoClick}
                    onItemClick={handleApplicationClick}
                    currentUserId={currentUser?.user_id}
                    likesOverride={getLikes(application)}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Received Applications Grid */}
      {!isLoading && activeTab === "received" && (
        <>
          {filteredReceivedApplications.length === 0 ? (
            <EmptyState
              type={
                allReceivedApplications.length === 0
                  ? "no-received-applications"
                  : "no-results"
              }
              title={
                allReceivedApplications.length === 0
                  ? undefined
                  : "No matching applications"
              }
              description={
                allReceivedApplications.length === 0
                  ? undefined
                  : "Try adjusting your filters to see more results."
              }
            />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredReceivedApplications.map((application) => (
                <div key={application.application_id} className="space-y-2">
                  <ApplicationGridItem
                    application={application}
                    type="received"
                    onVideoClick={handleVideoClick}
                    onItemClick={handleApplicationClick}
                    currentUserId={currentUser?.user_id}
                    likesOverride={getLikes(application)}
                    onToggleLike={handleLikeToggle}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Video Modal */}
      <FullscreenVideoModal
        isOpen={videoModalOpen}
        videoUrl={selectedVideoUrl}
        onClose={handleCloseVideoModal}
        authToken={authToken}
      />
    </PageLayout>
  );
};

export default ApplicationsPlaceholder;
